import React from 'react';
import { Row, Col, Button, OverlayTrigger, Popover } from 'react-bootstrap';

import Collapsible from 'react-collapsible';
import { BsChevronDown } from 'react-icons/bs';
import EquipmentState from './EquipmentState';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';

import styles from './GenericEquipmentControl.module.css';

// Remove underscores and hyphens, capitalise the first letter of each word.
const formatLabel = (value = '') =>
  value
    .replaceAll(/[_-]+/gu, ' ')
    .trim()
    .replaceAll(/\b\w/gu, (char) => char.toUpperCase());

function singleFieldTemplate(props) {
  const { classNames, help, description, errors, children } = props;
  return (
    <div className={classNames}>
      {description}
      {children}
      {errors}
      {help}
    </div>
  );
}

export default function GenericEquipmentControl(props) {
  const { equipment, executeCommand } = props;
  const isReady = equipment.state === 'READY';

  function handleRunCommand(cmd, formData) {
    executeCommand(equipment.name, cmd, formData);
  }

  function renderParameters(key) {
    const attr = equipment.commands[key];
    const displayName = formatLabel(key);

    if (attr.signature.length > 0) {
      const schema = JSON.parse(attr.schema);
      const fieldCount = Object.keys(schema.properties || {}).length;
      const singleFieldForm = fieldCount === 1;
      const uiSchema = {
        value: {
          'ui:label': false,
        },
      };
      const formSchema = {
        ...schema,
        title: '',
      };

      return (
        <div>
          <Form
            validator={validator}
            onSubmit={(formData, e) => handleRunCommand(key, formData.formData)}
            disabled={!isReady}
            schema={formSchema}
            uiSchema={uiSchema}
            templates={singleFieldForm ? { FieldTemplate: singleFieldTemplate } : {}}
          >
            <Button className="mt-3" variant="outline-secondary" type="submit">
              <b>Run {displayName}</b>
            </Button>
          </Form>
        </div>
      );
    }

    return (
      <span>
        <h3>(No arguments)</h3>
        <Button
          className="mt-3"
          variant="outline-secondary"
          type="submit"
          onClick={(e) => handleRunCommand(key, {})}
        >
          <b>Run {displayName}</b>
        </Button>
      </span>
    );
  }

  function renderCommands() {
    return Object.entries(equipment.commands).map(([key, cmdObj]) => {
      if (!cmdObj.display) {
        return null;
      }

      const displayName = formatLabel(key);
      // disable setting commands if equipment is not ready
      return (
        <span key={key}>
          {isReady ? (
            <OverlayTrigger
              trigger={['click']}
              placement="right"
              overlay={
                <Popover className={styles.cmdPopover}>
                  <Popover.Header as="h3">
                    <b>{displayName}</b>
                  </Popover.Header>
                  <Popover.Body>
                    <Row className={styles.cmdPopoverBody}>
                      <Col className="col-xs-4">{renderParameters(key)}</Col>
                      <Col className="col-xs-8">
                        <div>
                          <h3>Last result</h3>
                          <pre>{equipment.msg}</pre>
                        </div>
                      </Col>
                    </Row>
                  </Popover.Body>
                </Popover>
              }
              rootClose
            >
              <Button className="mt-3" variant="outline-secondary" type="submit">
                <b>{displayName}</b>
              </Button>
            </OverlayTrigger>
          ) : (
            <Button
              className="mt-3"
              variant="outline-secondary"
              type="button"
              disabled
              title="Equipment is busy"
            >
              <b>{displayName}</b>
            </Button>
          )}
        </span>
      );
    });
  }

  return (
    <Row className="mb-3">
      <Col sm={12} className={styles.container}>
        <Collapsible
          triggerClassName={styles.trigger}
          triggerOpenedClassName={styles.trigger}
          trigger={
            <>
              <EquipmentState
                state={equipment.state}
                equipmentName={formatLabel(equipment.name)}
              />
              <BsChevronDown className={styles.chevron} size="1em" />
            </>
          }
        >
          <div className={styles.content}>{renderCommands()}</div>
        </Collapsible>
      </Col>
    </Row>
  );
}
